import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import * as XLSX from "xlsx";
import Select from "react-select";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import EmailTemplates from "./reusable/EmailTemplates";
import toast from "react-hot-toast";

const safeDate = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const Emails = () => {
  const [emailCc, setEmailCc] = React.useState("");
  const [emailBcc, setEmailBcc] = React.useState("");
  const [sendMode, setSendMode] = useState("changed"); // or "all"

  const [dbData, setDbData] = useState(null);
  const emailBodyRef = React.useRef(null);
  const [emailBodySelectionStart, setEmailBodySelectionStart] =
    React.useState(null);
  const [emailBodySelectionEnd, setEmailBodySelectionEnd] =
    React.useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [editedData, setEditedData] = React.useState({});
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [locationFilter, setLocationFilter] = useState("");
  const [dialCodeFilter, setDialCodeFilter] = useState("");
  const [debouncedLocationFilter, setDebouncedLocationFilter] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [serviceTabs, setServiceTabs] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [newServiceName, setNewServiceName] = useState("");
  const [copyMasterData, setCopyMasterData] = useState(true);

  const handleStatusChange = (index, value) => {
    setEditedData((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        status: value,
        original: dbData[index],
      },
    }));
  };

  const handlePlatinumChange = (index, value) => {
    setEditedData((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        platinumUSD: value,
      },
    }));
  };

  const handleDateChange = (index, date) => {
    setEditedData((prev) => {
      const original = dbData[index];

      return {
        ...prev,
        [index]: {
          ...prev[index],
          original,
          platinumUSD: prev[index]?.platinumUSD ?? original.platinumUSD,
          status: prev[index]?.status ?? original.status,
          effectiveDate: date,
        },
      };
    });
  };

  const fetchDbData = async (pageNumber = 1, pageLimit = limit) => {
    setDbData(null);
    try {
      const params = new URLSearchParams();
      params.append("page", pageNumber);
      params.append("limit", pageLimit);
      params.append("type", activeTab); // Pass the tab type to backend
      if (debouncedLocationFilter)
        params.append("locationFilter", debouncedLocationFilter);
      if (dialCodeFilter) params.append("dialCodeFilter", dialCodeFilter);

      const res = await axios.get(
        `https://rate.tapdesk.biz/api/get-services?${params}`
      );
      setDbData(res.data.data);
      setTotalPages(res.data.totalPages);
      setPage(res.data.page);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`https://rate.tapdesk.biz/api/accounts`);
      setCompanies(res.data); // Store full company objects
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };
  useEffect(() => {
    fetchDbData(1, limit); // reset to page 1 on tab/filter change
  }, [activeTab, locationFilter, dialCodeFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLocationFilter(locationFilter.trim().toLowerCase());
    }, 400); // adjust debounce delay as needed

    return () => clearTimeout(handler);
  }, [locationFilter]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const companyOptions = companies.map((company) => ({
    value: company._id,
    label: company.name,
    email: company.email || "",
    billingEmail: company.billingEmail || "",
    salesEmail: company.salesEmail || "",
  }));
  useEffect(() => {
    if (selectedCompany) {
      setEmailTo(
        selectedCompany.billingEmail ||
          selectedCompany.salesEmail ||
          selectedCompany.email ||
          ""
      );
    }
  }, [selectedCompany]);

  const changedEntries = Object.entries(editedData);

  useEffect(() => {
    fetchDbData(1, limit); // Reset to first page on mount
  }, [limit]);

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const [selectedLocation, setSelectedLocation] = React.useState(null);
  const [bulkEditData, setBulkEditData] = React.useState({
    platinumUSD: "",
    status: "",
    effectiveDate: null,
  });
  const [showBulkEditModal, setShowBulkEditModal] = React.useState(false);

  const handleSelectLocation = (locationName) => {
    if (selectedLocation === locationName) {
      setSelectedLocation(null);
      setShowBulkEditModal(false);
    } else {
      setSelectedLocation(locationName);
      // Optionally prefill bulk edit data with first matched entry
      const firstEntry = dbData.find((d) => d.locationName === locationName);
      setBulkEditData({
        platinumUSD: firstEntry?.platinumUSD || "",
        status: firstEntry?.status || "no change",
        effectiveDate: firstEntry?.effectiveDate
          ? new Date(firstEntry.effectiveDate)
          : null,
      });
      setShowBulkEditModal(true);
    }
  };
  useEffect(() => {
    const formattedTab = activeTab
      ? activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
      : "";

    const modeLabel = sendMode === "changed" ? "Changed Rates" : "Full Sheet";

    setEmailSubject(`${formattedTab} Services – ${modeLabel}`);
  }, [activeTab, sendMode]);

  const applyBulkChanges = async () => {
    if (!selectedLocation || !activeTab) return;

    try {
      const response = await fetch(
        `https://rate.tapdesk.biz/api/get-services-by-location?type=${activeTab}&location=${encodeURIComponent(
          selectedLocation
        )}`
      );

      const result = await response.json();
      console.log("📡 API raw result:", result);

      const data = Array.isArray(result) ? result : result?.data || [];
      console.log("📡 Parsed data:", data);

      if (!Array.isArray(data)) {
        toast.error("Invalid response from server.");
        return;
      }

      setEditedData((prev) => {
        const newData = { ...prev };

        data.forEach((entry) => {
          newData[entry.id] = {
            // 👈 _id ke badle id
            original: entry, // original data
            platinumUSD: bulkEditData.platinumUSD,
            status: bulkEditData.status,
            effectiveDate: bulkEditData.effectiveDate,
          };
        });

        console.log("✅ EditedData after bulk apply:", newData);
        return newData;
      });

      setShowBulkEditModal(false);
      setSelectedLocation(null);
    } catch (error) {
      console.error("❌ Bulk update fetch error:", error);
      toast.error("Failed to fetch data for bulk update.");
    }
  };

  const PaginationControls = () => {
    const generatePageNumbers = () => {
      const pages = [];

      // Always show first two and last two pages
      const firstPages = [1, 2];
      const lastPages = [totalPages - 1, totalPages];

      // If total pages are 6 or fewer, just show all
      if (totalPages <= 6) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
        return pages;
      }

      // Add first pages
      pages.push(...firstPages);

      // Add ellipsis if needed
      if (page > 4) pages.push("...");

      // Add current, previous, next pages
      const middlePages = [page - 1, page, page + 1].filter(
        (p) => p > 2 && p < totalPages - 1
      );
      pages.push(...new Set(middlePages));

      // Add ellipsis if needed before last pages
      if (page < totalPages - 3) pages.push("...");

      // Add last pages
      pages.push(...lastPages);

      return pages;
    };

    const pageNumbers = generatePageNumbers();

    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <label className="text-gray-700 mr-2">Rows per page:</label>
          <select
            value={limit}
            onChange={handleLimitChange}
            className="px-3 py-1 border rounded text-sm"
          >
            {[20, 40, 60, 80, 100, 200, 300, 400, 500].map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center flex-wrap gap-2">
          <button
            onClick={() => fetchDbData(page - 1, limit)}
            disabled={page === 1}
            className="text-xl px-2 py-1 rounded hover:bg-gray-200 disabled:text-gray-300"
          >
            ←
          </button>

          {pageNumbers.map((pageNum, idx) =>
            pageNum === "..." ? (
              <span key={idx} className="px-2 text-gray-500">
                ...
              </span>
            ) : (
              <button
                key={pageNum}
                onClick={() => fetchDbData(pageNum, limit)}
                className={`px-3 py-1 rounded border text-sm font-medium ${
                  pageNum === page
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {pageNum}
              </button>
            )
          )}

          <button
            onClick={() => fetchDbData(page + 1, limit)}
            disabled={page === totalPages}
            className="text-xl px-2 py-1 rounded hover:bg-gray-200 disabled:text-gray-300"
          >
            →
          </button>
        </div>
      </div>
    );
  };

  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [showDialCodeSearch, setShowDialCodeSearch] = useState(false);

  const locationInputRef = useRef(null);

  const dialCodeInputRef = useRef(null);

  // Auto-focus input when it becomes visible
  useEffect(() => {
    if (showLocationSearch && locationInputRef.current) {
      locationInputRef.current.focus();
    }
  }, [showLocationSearch]);

  useEffect(() => {
    if (showDialCodeSearch && dialCodeInputRef.current) {
      dialCodeInputRef.current.focus();
    }
  }, [showDialCodeSearch]);

  // Close search inputs on Escape key press
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        setShowLocationSearch(false);
        setShowDialCodeSearch(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close search inputs on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showLocationSearch &&
        locationInputRef.current &&
        !locationInputRef.current.contains(event.target) &&
        !event.target.closest("#location-search-icon")
      ) {
        setShowLocationSearch(false);
      }
      if (
        showDialCodeSearch &&
        dialCodeInputRef.current &&
        !dialCodeInputRef.current.contains(event.target) &&
        !event.target.closest("#dialcode-search-icon")
      ) {
        setShowDialCodeSearch(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLocationSearch, showDialCodeSearch]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // New state for email editor modal & fields
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleConfirm = () => {
    if (!selectedTemplate) return;

    // Build locations list again (if not stored globally)
    const changedLocations = changedEntries.map(([index, data]) => {
      const edited = editedData[index];
      return edited?.locationName || data.original?.locationName;
    });

    const locationList = [...new Set(changedLocations.filter(Boolean))];

    const filledSubject = selectedTemplate.subject;
    const filledBody = selectedTemplate.getBody(locationList); // ✅ call getBody()

    setEmailTo(selectedCompany?.email || "");
    setEmailSubject(filledSubject);
    setEmailBody(filledBody);

    closeModal();
    setShowEmailEditor(true);
  };

  const handleSendEmail = async (excelFile) => {
    const formData = new FormData();

    // Only what user typed in editor
    if (emailTo) formData.append("to", emailTo);
    if (emailCc) formData.append("cc", emailCc); // No forced billing/sales
    if (emailBcc) formData.append("bcc", emailBcc);

    formData.append("subject", emailSubject);
    formData.append("text", emailBody);

    if (excelFile) {
      formData.append("attachment", excelFile);
    }

    try {
      const response = await fetch(
        "https://rate.tapdesk.biz/api/send-email",
        { method: "POST", body: formData }
      );

      const result = await response.json;
      if (response.ok) {
        toast.success("Email Sent Successfully!");
        setShowEmailEditor(false);
      } else {
        toast.error("Failed to send email: " + result.message);
      }
    } catch (err) {
      console.error("Email send error:", err);
      toast.error("Error Occurred. Please try again later!");
    }
  };

  const generateChangedExcelFile = () => {
    const worksheetData = changedEntries.map(([index, data]) => {
      const editedEntry = editedData[index] || {};
      return {
        // previously Location
        Destination:
          editedEntry.locationName || data.original?.locationName || "—",
        // previously DialCode
        "Country Code": editedEntry.dialCode || data.original?.dialCode || "—",
        // previously Platinum USD
        "Price USD":
          editedEntry.platinumUSD !== undefined
            ? editedEntry.platinumUSD
            : data.original?.platinumUSD || "",
        // previously New Status
        Status: editedEntry.status || data.status,
        // same as before
        "Effective Date":
          editedEntry.effectiveDate ||
          data.effectiveDate?.toISOString().split("T")[0] ||
          "—",
      };
    });

    return buildExcelFile(worksheetData, "Rate Sheet");
  };

  const fetchFullDbData = async () => {
    try {
      const res = await fetch(
        `https://rate.tapdesk.biz/api/get-services?type=${activeTab}&limit=100000`
      );
      const json = await res.json();
      return json.data || [];
    } catch (err) {
      console.error("Error fetching full DB data:", err);
      return [];
    }
  };

  // Add this state at the top with other useState hooks
  const [generatedExcelFile, setGeneratedExcelFile] = useState(null);

  const generateFullExcelFile = async (tableName) => {
    try {
      // 1. Fetch all data from SQL
      const fullDbData = await fetchFullDbData(tableName);

      // 2. Chunk it to avoid memory crash
      const chunkSize = 10000;
      const workbook = XLSX.utils.book_new();

      for (let i = 0; i < fullDbData.length; i += chunkSize) {
        const chunk = fullDbData.slice(i, i + chunkSize);
        const worksheetData = chunk.map((entry) => ({
          Location: entry.locationName || "—",
          DialCode: entry.dialCode || "—",
          "Platinum USD": entry.platinumUSD ?? "",
          Status: entry.status || "—",
          "Effective Date": entry.effectiveDate
            ? entry.effectiveDate.split("T")[0]
            : "—",
        }));

        const sheet = XLSX.utils.json_to_sheet(worksheetData);
        const sheetName = `Sheet${i / chunkSize + 1}`;
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
      }

      // 3. Convert workbook to file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const file = new File([blob], "rate_sheet.xlsx", { type: blob.type });

      setGeneratedExcelFile(file);
      toast.success("Full database exported successfully.");
      return file;
    } catch (err) {
      console.error("Failed to generate full Excel:", err);
      toast.error("Failed to export full database.");
      return null;
    }
  };

  const buildExcelFile = (worksheetData, sheetName) => {
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const file = new File([blob], `${sheetName.replace(" ", "_")}.xlsx`, {
      type: blob.type,
    });

    return file;
  };

  const handleFinalSend = async () => {
    let fileToSend;

    try {
      setSendLoader(true); // 👈 loader on

      if (sendMode === "changed") {
        fileToSend = generateChangedExcelFile(); // returns File
      } else if (sendMode === "all") {
        fileToSend = await generateFullExcelFile(); // async
      } else {
        toast.error("Please select a valid export mode.");
        return;
      }

      if (fileToSend) {
        await handleSendEmail(fileToSend);
        toast.success("Email sent successfully!");
      } else {
        toast.error("Failed to generate file for sending.");
      }
    } catch (err) {
      console.error("❌ Error sending:", err);
      toast.error("Failed to send email");
    } finally {
      setSendLoader(false); // 👈 loader off
    }
  };

  const [sendLoader, setSendLoader] = useState(false);

  const handleDelete = async ({ ids = [], locationName = "" } = {}) => {
    if (!activeTab) return;

    const deleteType =
      ids.length > 0 ? "byIds" : locationName ? "byLocation" : "bulk";

    const confirmMessage =
      deleteType === "byIds"
        ? `Delete ${ids.length} entries?`
        : deleteType === "byLocation"
        ? `Delete all entries with location "${locationName}"?`
        : `Delete ALL entries in "${activeTab.toUpperCase()}" tab?`;

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      const res = await axios.delete(
        `https://rate.tapdesk.biz/api/delete-services?type=${activeTab}`,
        {
          data: ids.length ? { ids } : locationName ? { locationName } : {},
        }
      );

      toast.success(`Deleted ${res.data.deletedCount} entries successfully.`);
      fetchDbData(); // refresh table
    } catch (err) {
      console.error("Deletion failed:", err);
      toast.error("Failed to delete entries.");
    }
  };

  const updateDatabase = async () => {
    const DEBUG = true;

    try {
      console.log("🔔 [DEBUG] Starting updateDatabase");
      console.log("🔎 [DEBUG] selectedCompany:", selectedCompany);
      console.log("🔎 [DEBUG] activeTab:", activeTab);
      console.log(
        "🔎 [DEBUG] editedData (sample):",
        Object.keys(editedData).slice(0, 10)
      );

      const updatePayload = Object.entries(editedData)
        .map(([key, entry]) => {
          const original = paginatedEntries.find(
            ([id, data]) => id === key
          )?.[1]?.original;

          const dialCode = entry.dialCode || original?.dialCode;
          if (!dialCode) return null;

          const obj = {
            dialCode,
            platinumUSD: entry.platinumUSD ?? original?.platinumUSD ?? 0,
            status: entry.status ?? original?.status ?? null,
            effectiveDate:
              entry.effectiveDate ?? original?.effectiveDate ?? null,
          };
          if (DEBUG) console.log("  [DEBUG] prepared update item:", obj);
          return obj;
        })
        .filter(Boolean);

      console.log("🔎 [DEBUG] updatePayload (count):", updatePayload.length);

      if (updatePayload.length === 0) {
        toast.error("No valid entries to update");
        return;
      }

      // Make sure serviceName extraction matches backend convention:
      const serviceNameToSend =
        activeTab?.split("_service_")?.[0] || activeTab?.split("_")?.[0] || "";
      const payload = {
        companyName: selectedCompany.name,
        serviceName: serviceNameToSend,
        // optional: you can also send the full tab (table name) to backend:
        fullServiceName: activeTab || undefined,
        updates: updatePayload,
      };

      console.log("📤 [DEBUG] POST payload:", payload);

      const url = "https://rate.tapdesk.biz/api/update-service-entries";

      const response = await axios.post(url, payload);
      console.log("✅ [DEBUG] Response:", response?.data);

      toast.success(response?.data?.message || "Update successful");
    } catch (err) {
      // more verbose error logging
      console.error("❌ Failed to update service entries:", {
        message: err.message,
        code: err.code,
        config: err.config,
        request: err.request,
        responseData: err.response?.data,
        responseStatus: err.response?.status,
        responseHeaders: err.response?.headers,
      });

      // show clearer toast
      const friendly =
        err.response?.data?.message || err.message || "Update failed";
      toast.error(`Update failed: ${friendly}`);
    }
  };

  const [createServiceLoader, setcreateServiceLoader] = useState(false);
  const handleCreateService = async () => {
    if (!newServiceName.trim()) {
      toast.warning("Please enter a valid service name.");
      return;
    }

    if (!selectedCompany) {
      toast.warning("Please select a company first.");
      return;
    }

    setcreateServiceLoader(true);

    try {
      const { data } = await axios.post(
        "https://rate.tapdesk.biz/api/create-service-type",
        {
          serviceName: newServiceName.toLowerCase(),
          companyName: selectedCompany.name.toLowerCase(),
        }
      );

      toast.success("Service creation started successfully!");

      // ✅ Refresh services from backend and set new tab as active
      const res = await axios.get(
        "https://rate.tapdesk.biz/api/get-company-services",
        {
          params: { companyName: selectedCompany.name.toLowerCase() },
        }
      );

      setServiceTabs(res.data.services);
      setActiveTab(data.collection); // active tab ko latest service pe le aao

      // Reset modal
      setNewServiceName("");
      setCopyMasterData(true);
      setShowModal(false);
    } catch (error) {
      console.error("Failed to create service:", error);
      toast.error("Failed to start service creation.");
    } finally {
      setcreateServiceLoader(false);
    }
  };

  const [changedPage, setChangedPage] = useState(1);
  const [changedLimit, setChangedLimit] = useState(20);

  const totalPage = Math.ceil(changedEntries.length / changedLimit);
  const paginatedEntries = changedEntries.slice(
    (changedPage - 1) * changedLimit,
    changedPage * changedLimit
  );
  const changedLocations = changedEntries.map(([index, data]) => {
    const edited = editedData[index];
    return edited?.locationName || data.original?.locationName;
  });

  const locationList = [...new Set(changedLocations.filter(Boolean))];
  useEffect(() => {
    if (!activeTab) return;

    const base = activeTab.split("_")[0] || "";
    const formatted = base ? base.charAt(0).toUpperCase() + base.slice(1) : "";

    setEmailSubject(`Changed Rate - ${formatted}`);
  }, [activeTab]);

  useEffect(() => {
    if (selectedCompany) {
      const ccArray = [];

      if (selectedCompany.billingEmail)
        ccArray.push(selectedCompany.billingEmail);
      if (selectedCompany.salesEmail) ccArray.push(selectedCompany.salesEmail);

      setEmailCc(ccArray.join(", "));
    }
  }, [selectedCompany]);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-lg p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          Manage Service Pricing
        </h1>
        <Select
          options={companyOptions}
          onChange={(selected) => {
            const companyObj = {
              _id: selected.value,
              name: selected.label,
              email: selected.email,
              billingEmail: selected.billingEmail || "",
              salesEmail: selected.salesEmail || "",
            };

            setSelectedCompany(companyObj);

            // 🔥 Fetch service tabs for the selected company
            axios
              .get("https://rate.tapdesk.biz/api/get-company-services", {
                params: { companyName: selected.label.toLowerCase() },
              })
              .then((res) => {
                setServiceTabs(res.data.services); // fill the tabs
                setActiveTab(res.data.services[0] || ""); // auto-select first one
              })
              .catch((err) => {
                console.error(
                  "Services doesn't exist. Please create one.",
                  err
                );
                toast.error("Services doesn't exist. Please create one.");
                setServiceTabs([]); // fallback
                setActiveTab("");
              });
          }}
          placeholder="Select or search company..."
          className="mb-4"
        />
        {selectedCompany && (
          <div className="p-4 bg-gray-100 rounded mb-3">
            <p className="text-gray-700">
              <strong>Company:</strong> {selectedCompany.name}
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> {selectedCompany.email}
            </p>
            {selectedCompany.billingEmail && (
              <div>
                <strong> Billing Email: </strong>
                {selectedCompany.billingEmail}
              </div>
            )}
            {selectedCompany.salesEmail && (
              <div>
                <strong> Sales Email: </strong>
                {selectedCompany.salesEmail}
              </div>
            )}
          </div>
        )}

        {/* Tabs + Create Button */}
        <div className="mb-8 border-b border-gray-200 flex justify-between items-center">
          <div className="flex space-x-6 text-base font-medium text-gray-500">
            {serviceTabs.map((tab) => {
              if (!tab) return null;

              // Always safe split
              const parts = tab.split("_service_");
              const displayName = parts[0] ? parts[0] : "";

              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`relative pb-3 transition duration-200 ease-in-out ${
                    activeTab === tab
                      ? "text-blue-600 after:absolute after:inset-x-0 after:-bottom-[1px] after:h-[3px] after:bg-blue-600 after:rounded-t-md"
                      : "hover:text-blue-500"
                  }`}
                >
                  {displayName
                    ? displayName.charAt(0).toUpperCase() + displayName.slice(1)
                    : ""}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => {
              if (!selectedCompany) {
                toast.error("Please select a company");
                return;
              }
              setShowModal(true);
            }}
            className="text-white bg-blue-600 px-4 py-2 rounded-md hover:bg-blue-700"
          >
            + Create Service
          </button>
        </div>

        {/* Create Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center">
            <div className="bg-white p-6 rounded-lg w-[90%] max-w-md shadow-lg">
              <h2 className="text-xl font-semibold mb-4">Create New Service</h2>

              <label className="block mb-2 font-medium">Service Name</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded mb-4"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                placeholder="e.g., whatsapp"
              />

              <label className="flex items-center space-x-2 mb-6">
                <input
                  type="checkbox"
                  checked={copyMasterData}
                  onChange={(e) => setCopyMasterData(e.target.checked)}
                />
                <span>Copy from Master Data</span>
              </label>

              <div className="flex justify-end space-x-3">
                <button
                  className="px-4 py-2 border rounded"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={handleCreateService}
                >
                  {createServiceLoader ? "Creating..." : "Create Service"}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab && (
          <>
            {/* Table Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-700">
                  {activeTab &&
                    `${(activeTab.split("_")[0] || "")
                      .charAt(0)
                      .toUpperCase()}${(activeTab.split("_")[0] || "").slice(
                      1
                    )}`}{" "}
                  Service
                </h2>

                <div className="flex gap-3">
                  {/* Delete by Location */}
                  <button
                    onClick={() => {
                      const location = prompt("Enter Country Name to delete:");
                      if (location) handleDelete({ locationName: location });
                    }}
                    className="bg-red-100 text-red-700 px-4 py-1.5 rounded hover:bg-red-200 text-sm"
                  >
                    Delete by Country
                  </button>

                  {/* Bulk Delete by selected IDs */}
                  <button
                    onClick={() => handleDelete()} // no ids or locationName → deletes all
                    className="bg-red-600 text-white px-4 py-1.5 rounded hover:bg-red-700 text-sm"
                  >
                    Delete All
                  </button>
                </div>
              </div>

              <>
                {/* Pagination Top */}
                <PaginationControls />

                <div className="overflow-x-auto border rounded-lg shadow-sm">
                  <table className="min-w-full table-bordered text-sm text-left text-gray-700">
                    <thead className=" text-black bg-gray-200 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-1 text-center">Bulk Select</th>
                        <th className="px-6 py-1 text-center relative">
                          <div className="flex items-center space-x-2">
                            {!showLocationSearch && (
                              <>
                                <span>Destination</span>
                                <button
                                  id="location-search-icon"
                                  onClick={() => setShowLocationSearch(true)}
                                  className="p-1 hover:text-blue-600 focus:outline-none"
                                  aria-label="Search Country Name"
                                >
                                  <MagnifyingGlassIcon className="h-5 w-5" />
                                </button>
                              </>
                            )}
                            {showLocationSearch && (
                              <input
                                type="text"
                                ref={locationInputRef}
                                value={locationFilter}
                                onChange={(e) =>
                                  setLocationFilter(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    setShowLocationSearch(false); // close on enter
                                  }
                                }}
                                placeholder="Search..."
                                className="border border-gray-300 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </div>
                        </th>

                        {/* Dial Code with search toggle */}
                        <th className="px-6 py-1 text-center relative">
                          <div className="flex items-center space-x-2">
                            {!showDialCodeSearch && (
                              <>
                                <span>Dial Code</span>
                                {/* <button
                                  id="dialcode-search-icon"
                                  onClick={() => setShowDialCodeSearch(true)}
                                  className="p-1 hover:text-blue-600 focus:outline-none"
                                  aria-label="Search Dial Code"
                                >
                                  <MagnifyingGlassIcon className="h-5 w-5" />
                                </button> */}
                              </>
                            )}
                            {showDialCodeSearch && (
                              <input
                                type="text"
                                ref={dialCodeInputRef}
                                value={dialCodeFilter}
                                onChange={(e) =>
                                  setDialCodeFilter(e.target.value)
                                }
                                placeholder="Search Dial Code..."
                                className="border border-gray-300 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </div>
                        </th>

                        <th className="px-6 py-1 text-center">Price $ USD</th>
                        <th className="px-6 py-1 text-center">Status</th>
                        <th className="px-6 py-1 text-center">
                          Effective Date
                        </th>
                      </tr>
                    </thead>
                    {dbData === null ? (
                      <div className="text-gray-500 py-6 text-center">
                        Loading...
                      </div>
                    ) : (
                      <tbody>
                        {dbData.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center py-6 text-gray-500"
                            >
                              No results found.
                            </td>
                          </tr>
                        ) : (
                          dbData.map((entry, index) => (
                            <tr
                              key={index}
                              className="hover:bg-gray-200 transition cursor-pointer odd:bg-white even:bg-blue-100 border-b text-black dark:border-gray-900"
                            >
                              <td className="px-4 py-1 text-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedLocation === entry.locationName
                                  }
                                  onChange={() =>
                                    handleSelectLocation(entry.locationName)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className="px-6 py-1 text-center">
                                {entry.locationName || "—"}
                              </td>
                              <td className="px-6 py-1 text-center">
                                {entry.dialCode || "—"}
                              </td>
                              <td className="px-6 py-1 text-center">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={
                                    editedData[index]?.platinumUSD !== undefined
                                      ? editedData[index].platinumUSD
                                      : entry.platinumUSD || ""
                                  }
                                  onChange={(e) =>
                                    handlePlatinumChange(index, e.target.value)
                                  }
                                  className="border rounded px-2 py-1 text-sm w-24"
                                  placeholder="Enter USD"
                                />
                              </td>
                              <td className="px-6 py-1 text-center">
                                <select
                                  value={
                                    editedData[index]?.status ??
                                    entry.status ??
                                    "no change"
                                  }
                                  onChange={(e) =>
                                    handleStatusChange(index, e.target.value)
                                  }
                                  className="border rounded px-2 py-1 text-sm"
                                >
                                  <option value="increase">Increase</option>
                                  <option value="decrease">Decrease</option>
                                  <option value="no change">No Change</option>
                                  <option value="new">New</option>
                                </select>
                              </td>
                              <td className="px-6 py-1 text-center">
                                <DatePicker
                                  selected={
                                    safeDate(
                                      editedData[index]?.effectiveDate
                                    ) || safeDate(entry.effectiveDate)
                                  }
                                  onChange={(date) =>
                                    handleDateChange(index, date)
                                  }
                                  className="border rounded px-2 py-1 text-sm"
                                  placeholderText="Select date"
                                  dateFormat="yyyy-MM-dd"
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    )}
                  </table>
                </div>

                {showBulkEditModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 space-y-4">
                      <h3 className="text-lg font-semibold">
                        Bulk Edit for Country: {selectedLocation}
                      </h3>

                      <div>
                        <label className="block mb-1 font-medium">
                          Platinum $ USD
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={bulkEditData.platinumUSD}
                          onChange={(e) =>
                            setBulkEditData((prev) => ({
                              ...prev,
                              platinumUSD: e.target.value,
                            }))
                          }
                          className="w-full border rounded px-3 py-2"
                          placeholder="Enter USD"
                        />
                      </div>

                      <div>
                        <label className="block mb-1 font-medium">Status</label>
                        <select
                          value={bulkEditData.status}
                          onChange={(e) =>
                            setBulkEditData((prev) => ({
                              ...prev,
                              status: e.target.value,
                            }))
                          }
                          className="w-full border rounded px-3 py-2"
                        >
                          <option value="increase">Increase</option>
                          <option value="decrease">Decrease</option>
                          <option value="no change">No Change</option>
                          <option value="new">New</option>
                        </select>
                      </div>

                      <div>
                        <label className="block mb-1 font-medium">
                          Effective Date
                        </label>
                        <DatePicker
                          selected={safeDate(bulkEditData.effectiveDate)}
                          onChange={(date) =>
                            setBulkEditData((prev) => ({
                              ...prev,
                              effectiveDate: date,
                            }))
                          }
                          className="w-full border rounded px-3 py-2"
                          placeholderText="Select date"
                          dateFormat="yyyy-MM-dd"
                        />
                      </div>

                      <div className="flex justify-end gap-4 mt-4">
                        <button
                          onClick={() => setShowBulkEditModal(false)}
                          className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={applyBulkChanges}
                          className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                        >
                          Apply Changes
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {paginatedEntries.length > 0 && (
                  <div className="mt-12">
                    {/* Pagination Footer (moved to top) */}
                    <div className="flex justify-between items-center mb-4">
                      {/* Rows per page */}
                      <div className="flex items-center gap-2 text-sm">
                        <span>Rows per page:</span>
                        <select
                          value={changedLimit}
                          onChange={(e) => {
                            setChangedLimit(Number(e.target.value));
                            setChangedPage(1);
                          }}
                          className="border px-2 py-1 rounded"
                        >
                          {[10, 20, 50, 100, 200, 500].map((val) => (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Page navigation */}
                      <div className="flex items-center gap-2 text-sm">
                        <button
                          onClick={() =>
                            setChangedPage((p) => Math.max(1, p - 1))
                          }
                          disabled={changedPage === 1}
                          className="px-3 py-1 border rounded disabled:opacity-40"
                        >
                          Prev
                        </button>
                        <span>
                          Page {changedPage} of {totalPage}
                        </span>
                        <button
                          onClick={() =>
                            setChangedPage((p) => Math.min(totalPage, p + 1))
                          }
                          disabled={changedPage === totalPage}
                          className="px-3 py-1 border rounded disabled:opacity-40"
                        >
                          Next
                        </button>
                      </div>
                    </div>

                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                      Changed Entries
                    </h2>
                    <table className="min-w-full text-sm table-bordered text-gray-700">
                      <thead className="text-black bg-gray-200 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-1 text-center">Destination</th>
                          <th className="px-4 py-1 text-center">Dial Code</th>
                          <th className="px-4 py-1 text-center">
                            Price USD
                          </th>
                          <th className="px-4 py-1 text-center">New Status</th>
                          <th className="px-4 py-1 text-center">
                            Effective Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedEntries.map(([id, data]) => {
                          const editedEntry = editedData[id] || {};
                          return (
                            <tr
                              key={data._id}
                              className="hover:bg-gray-200 transition cursor-pointer odd:bg-white even:bg-blue-100 border-b text-black dark:border-gray-900"
                            >
                              <td className="px-4 py-1 text-center">
                                {editedEntry.locationName ||
                                  data.original?.locationName ||
                                  "—"}
                              </td>
                              <td className="px-4 py-1 text-center">
                                {editedEntry.dialCode ||
                                  data.original?.dialCode ||
                                  "—"}
                              </td>
                              <td className="px-4 py-1 text-center">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={
                                    editedEntry.platinumUSD !== undefined
                                      ? editedEntry.platinumUSD
                                      : data.original?.platinumUSD || ""
                                  }
                                  onChange={(e) =>
                                    handlePlatinumChange(id, e.target.value)
                                  }
                                  className="border rounded px-2 py-1 text-sm w-24"
                                  placeholder="Enter USD"
                                />
                              </td>
                              <td className="px-4 py-1 text-center">
                                {editedEntry.status || data.status}
                              </td>
                              <td className="px-4 py-1 text-center">
                                {safeDate(
                                  editedEntry.effectiveDate
                                )?.toLocaleDateString() ||
                                  safeDate(
                                    data.effectiveDate
                                  )?.toLocaleDateString() ||
                                  "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    <button
                      id="sendExcelBtn"
                      onClick={async () => {
                        const btn = document.getElementById("sendExcelBtn");

                        // Show loader
                        const originalText = btn.innerHTML;
                        btn.innerHTML = "⏳ Sending...";
                        btn.disabled = true;

                        try {
                          await updateDatabase(); // First update entries
                          openModal(); // Then open the modal to send Excel
                        } catch (err) {
                          console.error(err);
                          alert("Failed to send Excel");
                        } finally {
                          // Restore button
                          btn.innerHTML = originalText;
                          btn.disabled = false;
                        }
                      }}
                      className="mt-6 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded shadow"
                    >
                      Send Edited Excel
                    </button>

                    {/* Pagination Controls */}
                  </div>
                )}

                {isModalOpen && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
                    onClick={closeModal}
                  >
                    <div
                      className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col relative"
                      onClick={(e) => e.stopPropagation()} // Prevent modal close on inner clicks
                    >
                      <h2 className="text-xl font-semibold mb-2 text-gray-900 tracking-wide border-b border-gray-200 pb-4">
                        Select Email Template
                      </h2>

                      <div className="overflow-y-auto pr-1 space-y-10 flex-1">
                        {EmailTemplates.map((template, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleTemplateSelect(template)}
                            className={`cursor-pointer rounded-xl border transition-all duration-200 ${
                              selectedTemplate === template
                                ? "bg-blue-50 border-blue-500 shadow-md ring-1 ring-blue-300"
                                : "border-gray-300 hover:shadow-md hover:bg-gray-50"
                            } p-5`}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleTemplateSelect(template);
                              }
                            }}
                          >
                            <h4 className="font-semibold text-xl text-gray-900 truncate">
                              {template.subject +
                                ` - ${(activeTab?.split("_")[0] || "")
                                  .charAt(0)
                                  .toUpperCase()}${(
                                  activeTab?.split("_")[0] || ""
                                ).slice(1)}`}
                            </h4>

                            <p className="text-sm text-gray-700 mt-2 whitespace-pre-line leading-relaxed">
                              {template.getBody(locationList)}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Sticky Footer */}
                      <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-4 sticky bottom-0 bg-white">
                        <button
                          onClick={closeModal}
                          className="px-6 py-2.5 rounded-lg bg-gray-100 text-gray-800 font-medium hover:bg-gray-200 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleConfirm}
                          disabled={!selectedTemplate}
                          className={`px-6 py-2.5 rounded-lg font-semibold text-white transition-all ${
                            selectedTemplate
                              ? "bg-blue-600 hover:bg-blue-700 shadow"
                              : "bg-blue-300 cursor-not-allowed"
                          }`}
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Email Editor Modal */}
                {showEmailEditor && (
                  <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-6">
                    <div
                      className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto p-10 flex flex-col text-gray-900"
                      style={{ minHeight: "600px" }}
                    >
                      <h2 className="text-3xl font-bold mb-8 tracking-tight border-b pb-4 border-gray-200">
                        Send Edited Email
                      </h2>

                      {/* To */}
                      <label className="block mb-2 font-medium text-gray-700">
                        To
                      </label>
                      <input
                        type="email"
                        value={emailTo}
                        onChange={(e) => setEmailTo(e.target.value)}
                        placeholder="Recipient email"
                        className="w-full px-5 py-3 mb-4 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />

                      {/* Toggle buttons for CC/BCC */}
                      <div className="flex gap-4 mb-4">
                        <button
                          type="button"
                          onClick={() => setShowCc((prev) => !prev)}
                          className="text-sm text-gray-700 hover:underline font-medium"
                        >
                          {showCc ? "Hide CC" : "Add CC"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowBcc((prev) => !prev)}
                          className="text-sm text-gray-700 hover:underline font-medium"
                        >
                          {showBcc ? "Hide BCC" : "Add BCC"}
                        </button>
                      </div>

                      {/* CC */}
                      {/* CC */}
                      {showCc && (
                        <>
                          <label className="block mb-2 font-medium text-gray-700">
                            CC
                          </label>
                          <input
                            type="text"
                            value={emailCc}
                            onChange={(e) => setEmailCc(e.target.value)}
                            placeholder="Add CC recipients, separated by commas"
                            className="w-full px-5 py-3 mb-4 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                          />
                        </>
                      )}

                      {/* BCC */}
                      {showBcc && (
                        <>
                          <label className="block mb-2 font-medium text-gray-700">
                            BCC
                          </label>
                          <input
                            type="text"
                            value={emailBcc}
                            onChange={(e) => setEmailBcc(e.target.value)}
                            placeholder="Add BCC recipients, separated by commas"
                            className="w-full px-5 py-3 mb-4 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                          />
                        </>
                      )}

                      {/* Subject */}
                      <label className="block mb-2 font-medium text-gray-700">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="Email subject"
                        className="w-full px-5 py-3 mb-6 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />

                      {/* Send Options */}
                      <div className="mb-6">
                        <label className="block mb-2 font-medium text-gray-700">
                          Select Export Type
                        </label>
                        <select
                          value={sendMode}
                          onChange={(e) => setSendMode(e.target.value)}
                          className="w-full px-5 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        >
                          <option value="changed">Only Changed Entries</option>
                          {/*<option value="all">Full Sheet with Changes</option>*/}
                        </select>
                      </div>

                      {/* Toolbar */}
                      <label className="block mb-2 font-medium text-gray-700">
                        Message
                      </label>

                      <textarea
                        ref={emailBodyRef}
                        rows={12}
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        onSelect={(e) => {
                          setEmailBodySelectionStart(e.target.selectionStart);
                          setEmailBodySelectionEnd(e.target.selectionEnd);
                        }}
                        placeholder="Email body"
                        className="w-full px-5 py-4 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y transition"
                        style={{ minHeight: "250px" }}
                      ></textarea>

                      {/* Footer Buttons */}
                      <div className="flex justify-end mt-10 space-x-4">
                        <button
                          onClick={() => setShowEmailEditor(false)}
                          className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition shadow-md"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleFinalSend}
                          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition shadow-md flex items-center gap-2"
                          disabled={sendLoader}
                        >
                          {sendLoader ? (
                            <>
                              <svg
                                className="animate-spin h-5 w-5 text-white"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8H4z"
                                ></path>
                              </svg>
                              Sending...
                            </>
                          ) : (
                            "Send"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Emails;
